import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { DocumentArrowDownIcon, FunnelIcon, PencilSquareIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import ButtonComponent from 'components/common/ButtonComponent'
import TableComponent from 'components/common/TableComponent'
import PickingDataEditModal from 'components/modals/PickingDataEditModal'
import PageLoadingState from 'components/common/PageLoadingState'
import ModalComponent from 'components/common/ModalComponent'
import DateRangeFilterModal from 'components/modals/DateRangeFilterModal'
import { parseDate } from 'utils'
import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'
import { useAuth } from 'auth/AuthContext'
import { DateTime } from 'luxon'
import { useDashboardDateRange } from 'hooks/useDashboardDateRange'
import { useDashboardFilterOptions } from 'hooks/useDashboardFilterOptions'
import { buildDashboardDateRangeQuery, getDateRangeSummary } from 'utils/dateRangeFilters'
import { exportDataEditExcel, exportDataEditPdf, filterRowsBySearch } from 'utils/dataEditExport'

const exportColumns = [
  { key: 'block', label: 'Block' }, { key: 'workerName', label: 'Worker' },
  { key: 'date', label: 'Date' }, { key: 'containerName', label: 'Container' },
  { key: 'totalUnits', label: 'Total Units' }, { key: 'credit', label: 'Credit' },
  { key: 'boxId', label: 'Box ID' }
]

export default function PickingDataEdit () {
  const { user } = useAuth()
  const { t } = useTranslation()
  const hasFetched = useRef(false)
  const currentFilter = useRef(null)
  const currentDate = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd')
  const [dateRange, setDateRange] = useDashboardDateRange(currentDate)
  const filterOptions = useDashboardFilterOptions(user?.farmId)
  const [showFilters, setShowFilters] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])
  const [state, setState] = useState({
    pickingData: [],
    searchTerm: '',
    showEditModal: false,
    selectedPickingData: null,
    isBulkEdit: false,
    loading: true,
    showDeleteConfirm: false,
    pendingDelete: null
  })

  const getPickingData = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/data-edit/picking-data/${user.farmId}?${buildDashboardDateRangeQuery(dateRange)}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      setState(s => ({ ...s, pickingData: response.data, loading: false }))
    } catch (error) {
      console.error('Error:', error)
      setState(s => ({ ...s, loading: false }))
    }
  }

  const openSingleEditModal = (row) => {
    setState(s => ({ ...s, showEditModal: true, selectedPickingData: row, isBulkEdit: false }))
  }

  const openBulkEditModal = () => {
    setState(s => ({ ...s, showEditModal: true, selectedPickingData: null, isBulkEdit: true }))
  }

  const closeModal = () => {
    setState(s => ({ ...s, showEditModal: false, selectedPickingData: null, isBulkEdit: false }))
  }

  const handleSave = async (formData) => {
    try {
      if (state.isBulkEdit) {
        const ids = selectedRows.map(r => r.pickingDataId)
        await makeRequest({
          method: 'PATCH',
          url: '/management/data-edit/picking-data/bulk',
          data: { ids, ...formData },
          headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
        })
      } else {
        await makeRequest({
          method: 'PATCH',
          url: `/management/data-edit/picking-data/${state.selectedPickingData.pickingDataId}`,
          data: formData,
          headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
        })
      }
      showAlert({ text: t('mgmt.pickingDataEdit.updated'), icon: 'success' })
      closeModal()
      await getPickingData()
    } catch (error) {
      showAlert({ text: t('mgmt.pickingDataEdit.updateFailed'), icon: 'error' })
      console.error('Error updating picking data:', error)
    }
  }

  const handleDelete = (row) => {
    setState(s => ({ ...s, showDeleteConfirm: true, pendingDelete: row }))
  }

  const confirmDelete = async () => {
    const row = state.pendingDelete
    setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))
    try {
      await makeRequest({
        method: 'DELETE',
        url: `/management/data-edit/picking-data/${row.pickingDataId}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      showAlert({ text: t('mgmt.pickingDataEdit.deleted'), icon: 'success' })
      await getPickingData()
    } catch (error) {
      showAlert({ text: t('mgmt.pickingDataEdit.deleteFailed'), icon: 'error' })
      console.error('Error deleting picking data:', error)
    }
  }

  const filteredData = filterRowsBySearch(state.pickingData, state.searchTerm)

  useEffect(() => {
    if (!user?.farmId) return
    const nextFilter = JSON.stringify(dateRange)
    if (hasFetched.current && currentFilter.current === nextFilter) return
    hasFetched.current = true
    currentFilter.current = nextFilter
    getPickingData()
  }, [user?.farmId, dateRange])

  if (state.loading) {
    return <PageLoadingState message={t('mgmt.pickingDataEdit.loading')} rows={8} />
  }

  return (
    <div className='p-6 bg-white shadow-md border border-gray-300 border-t-0'>
      <div className='section-header'>
        <div className='header-left'>
          <div className='date-filter'>
            <span>{getDateRangeSummary(dateRange)}</span>
          </div>
        </div>

        <div className='flex items-center gap-4 mb-6'>
          {selectedRows.length > 0 && (
            <ButtonComponent className='flex items-center gap-2' onClick={openBulkEditModal}>
              <PencilSquareIcon className='w-5 h-5' />
              <span>{t('mgmt.dataEdit.editSelection', { count: selectedRows.length })}</span>
            </ButtonComponent>
          )}
          <ButtonComponent className='flex items-center gap-2' onClick={() => exportDataEditExcel({ rows: filteredData, columns: exportColumns, fileName: `picking-data-${currentDate}`, sheetName: 'Picking Data' })}>
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>{t('common.exportExcel')}</span>
          </ButtonComponent>
          <ButtonComponent className='flex items-center gap-2' onClick={() => exportDataEditPdf({ rows: filteredData, columns: exportColumns, fileName: `picking-data-${currentDate}`, title: 'Picking Data' })}>
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>{t('mgmt.common.exportPdf')}</span>
          </ButtonComponent>
        </div>
      </div>

      <div className='search-container flex gap-4 mb-4'>
        <input
          type='text'
          placeholder={t('mgmt.dataEdit.search')}
          value={state.searchTerm}
          onChange={(e) => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className='w-48 mt-1 p-2 border rounded-md shadow-sm'
          data-align='left'
        />
        <ButtonComponent className='flex items-center gap-2' onClick={() => setShowFilters(true)}><FunnelIcon className='w-5 h-5' /><span>{t('dashboard.advancedFilters')}</span></ButtonComponent>
      </div>

      <div className='data-table'>
        {state.pickingData.length !== 0 && (
          <TableComponent
            tableData={{
              hasCheckbox: true,
              columns: [
                { key: 'block', labelKey: 'mgmt.dataEdit.cols.block' },
                { key: 'workerName', labelKey: 'mgmt.dataEdit.cols.worker' },
                { key: 'date', labelKey: 'mgmt.dataEdit.cols.date', cell: (value) => parseDate(value) },
                { key: 'containerName', labelKey: 'mgmt.dataEdit.cols.container' },
                { key: 'totalUnits', labelKey: 'mgmt.dataEdit.cols.totalUnits' },
                { key: 'credit', labelKey: 'mgmt.dataEdit.cols.credit' },
                { key: 'boxId', labelKey: 'mgmt.dataEdit.cols.boxId' },
                { key: 'actions', labelKey: 'mgmt.dataEdit.cols.manage' }
              ],
              rows: filteredData
            }}
            handleEdit={openSingleEditModal}
            handleDelete={handleDelete}
            onSelectionChange={setSelectedRows}
            defaultSortColumn='date'
          />
        )}
      </div>

      <PickingDataEditModal
        isOpen={state.showEditModal}
        handleClose={closeModal}
        handleSave={handleSave}
        selectedPickingData={state.selectedPickingData}
        isBulk={state.isBulkEdit}
        bulkCount={selectedRows.length}
        farmId={user.farmId}
      />
      <DateRangeFilterModal isOpen={showFilters} dateRange={dateRange} sectors={filterOptions.sectors} farmingBlocks={filterOptions.farmingBlocks} groups={filterOptions.groups} activities={filterOptions.activities} maxDate={currentDate} onClose={() => setShowFilters(false)} onApply={(nextRange) => { setDateRange(nextRange); setShowFilters(false) }} />

      <ModalComponent
        isOpen={state.showDeleteConfirm}
        onClose={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
        title={t('mgmt.pickingDataEdit.deleteTitle')}
        maxWidth='sm'
        hideDefaultFooter
      >
        <div className='flex flex-col items-center gap-4 py-2'>
          <ExclamationTriangleIcon className='w-12 h-12 text-red-400' />
          <p className='text-sm text-gray-700 text-center'>
            {state.pendingDelete?.date
              ? t('mgmt.pickingDataEdit.removeConfirmWithDate', { name: state.pendingDelete?.workerName ?? '', date: state.pendingDelete.date })
              : t('mgmt.pickingDataEdit.removeConfirm', { name: state.pendingDelete?.workerName ?? '' })}
            <br />
            <span className='text-gray-500'>{t('mgmt.common.confirmDelete')}</span>
          </p>
          <div className='flex justify-end gap-3 w-full mt-2'>
            <button
              type='button'
              onClick={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
              className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200 transition'
            >
              {t('mgmt.common.cancel')}
            </button>
            <button
              type='button'
              onClick={confirmDelete}
              className='px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600 transition'
            >
              {t('mgmt.common.delete')}
            </button>
          </div>
        </div>
      </ModalComponent>
    </div>
  )
}
